/* ---------------------------------------------------------------------------
   Bulk import of inventory rows, with the report first.

   Records arrived by editing the seed and writing a migration. This is the other
   way in — and it is deliberately two steps, because the dangerous thing about
   a bulk import is not that it fails, it is that it succeeds at something
   nobody meant.

   So the report comes first, always: what would be created, what would be
   updated and which fields would move, what would be rejected and why. The
   button that writes only appears once that report is on screen, and it is the
   same call with one flag flipped — the plan cannot describe something other
   than what the apply does.

   ── WHAT THE LAYOUT IS DOING, because it is not decoration ──

   THE REPORT OUTRANKS THE FILE. The first version gave ten rows of monospace to
   the CSV and eleven grey pixels to the verdict — so the thing being approved
   was the smallest text on screen and the thing already decided was the largest.
   The counts are now a row of figures at the head of the report, and once a plan
   exists the file text collapses to a disclosure: after you have chosen a file
   its text is reference, not the subject.

   THE PICKER IS A DROP TARGET. A CSV arrives from a spreadsheet export, and the
   gesture that follows an export is a drag. The same element takes a click, so
   nothing depends on the drag being discoverable — see `imgslot` in
   src/components/imageSlot.ts, which this follows.

   COLOUR ONLY WHERE IT MEANS SOMETHING. `0 rejected` is not a warning and is not
   painted like one; the figure turns critical when it is non-zero and not
   before. Same rule the screening bands keep.

   Rendered into src/main.ts's dialog layer beside the others.
   --------------------------------------------------------------------------- */

import { h } from '../core/dom.ts';
import { actions, type State } from '../core/store.ts';
import { button, scrim } from '../components/widgets.ts';
import type { ImportPlan, ImportPlanRow } from '../core/api.ts';

/**
 * How a verdict is toned.
 *
 * FOUR VERDICTS, THREE TONES, and the pairing is the point. `create` and
 * `update` are both "this will be written" and share the accent; `unchanged` is
 * the register already agreeing and recedes; `reject` is the only one that stops
 * the import and is the only one that gets the critical tone. Ranking them on
 * one hue rather than four colours is the rule at `.tag--1`…`.tag--4`.
 */
const VERDICT_TONE: Record<ImportPlanRow['verdict'], string> = {
  create: 'imp-v--write',
  update: 'imp-v--write',
  unchanged: 'imp-v--same',
  reject: 'imp-v--stop',
};

/**
 * What to print in the outcome column.
 *
 * THE VERDICT IS ALREADY A PILL, so repeating it in prose is a column that costs
 * a third of the table and says nothing — "create · New structure." was the
 * first version. A create says nothing further unless the planner had something
 * to add; an update is its list of changed fields, which is the whole reason a
 * dry run exists; a rejection is its reason, always.
 */
function outcome(r: ImportPlanRow): string | null {
  if (r.verdict === 'create') {
    return /^new structure\.?$/i.test(r.reason.trim()) ? null : r.reason;
  }
  return r.reason || null;
}

function verdictRow(r: ImportPlanRow): HTMLElement {
  const said = outcome(r);
  return h(
    'tr',
    { class: r.verdict === 'unchanged' ? 'is-muted' : '' },
    h('td', { class: 'imp-line', text: String(r.line) }),
    h('td', { class: 'imp-id', text: r.id || '—' }),
    h(
      'td',
      { class: 'imp-verdict' },
      h('span', { class: `imp-v ${VERDICT_TONE[r.verdict]}`, text: r.verdict }),
    ),
    h(
      'td',
      {},
      said ? h('span', { text: said }) : null,
      /* The fields that would move, spelled out. A count of changes is not a
         report — "3 fields would change" is exactly the sentence somebody
         approves without reading. */
      r.changes.length
        ? h(
            'div',
            { class: 'imp-changes' },
            /* A field being SET has no "from", and a struck-through dash in
               that slot reads as a value that was deleted. Where the register
               held nothing, the chip is just `field → value`. */
            ...r.changes.map((c) =>
              h(
                'span',
                { class: 'imp-change' },
                h('b', { text: c.field }),
                c.oldValue == null
                  ? null
                  : h('span', { class: 'imp-change__from', text: String(c.oldValue) }),
                h('span', { class: 'imp-change__arrow', text: '→' }),
                h('span', { text: String(c.newValue ?? 'not recorded') }),
              ),
            ),
          )
        : null,
      /* Nothing further to say, and NOTHING DRAWN rather than a dash: a create
         is fully described by its verdict, and a column of dashes down every
         create row is furniture that makes the updates harder to find. */
    ),
  );
}

/**
 * The four figures the whole file reduces to.
 *
 * A FIGURE AND ITS WORD, not a run-on sentence in hint grey. The one that can
 * stop the import is the one allowed to shout, and only when it is not zero.
 */
function tally(plan: ImportPlan): HTMLElement {
  const cells: { n: number; label: string; tone?: string }[] = [
    { n: plan.counts.create, label: 'to create' },
    { n: plan.counts.update, label: 'to update' },
    { n: plan.counts.unchanged, label: 'already match', tone: 'imp-fig--quiet' },
    {
      n: plan.counts.reject,
      label: 'rejected',
      ...(plan.counts.reject > 0 ? { tone: 'imp-fig--stop' } : { tone: 'imp-fig--quiet' }),
    },
  ];
  return h(
    'div',
    { class: 'imp-tally', role: 'group', 'aria-label': 'What this file would do' },
    ...cells.map((c) =>
      h(
        'div',
        { class: ['imp-fig', c.tone] },
        h('div', { class: 'imp-fig__n', text: String(c.n) }),
        h('div', { class: 'imp-fig__l', text: c.label }),
      ),
    ),
  );
}

/** The dialog, or null when it is closed. */
export function importDialog(s: Readonly<State>): HTMLElement | null {
  if (!s.importOpen) return null;

  const plan = s.importPlan;
  const rejected = plan?.counts.reject ?? 0;
  const applied = plan?.applied === true;
  const writes = plan ? plan.counts.create + plan.counts.update : 0;
  const hasText = s.importCsv.trim() !== '';

  /** Take a file from the picker or from a drop, either way. */
  const accept = (file: File | undefined | null): void => {
    if (!file) return;
    void file.text().then((text) => actions.setImportCsv(text));
  };

  return h(
    'div',
    { class: 'dlg', ...scrim(() => actions.closeImport(), () => s.importBusy) },
    h(
      'div',
      {
        class: 'dlg__panel',
        role: 'dialog',
        'aria-modal': 'true',
        'aria-label': 'Import inventory rows',
      },

      h(
        'div',
        { class: 'dlg__head' },
        h(
          'div',
          {},
          h('div', { class: 'dlg__kicker', text: 'Register · bulk import' }),
          h('h2', { class: 'dlg__title', text: 'Import inventory rows' }),
        ),
        h('span', { class: 'spacer' }),
        h(
          'button',
          {
            type: 'button',
            class: 'dlg__close',
            title: 'Close',
            'aria-label': 'Close',
            onclick: () => actions.closeImport(),
          },
          '×',
        ),
      ),

      s.importError
        ? h('div', { class: 'dlg__note dlg__note--error' }, h('span', { text: s.importError }))
        : null,

      applied
        ? h(
            'div',
            { class: 'dlg__note dlg__note--done' },
            h('span', {
              text: `Imported. ${plan!.created ?? 0} created, ${plan!.updated ?? 0} updated. Every change is on each record's history.`,
            }),
          )
        : null,

      h(
        'div',
        { class: 'dlg__body' },

        /* ---- the file ---- */
        h(
          'button',
          {
            type: 'button',
            class: 'imp-drop',
            'aria-label': 'Choose a CSV file, or drop one here',
            /* Reached through the event rather than closed over, for the reason
               imageSlot.ts gives: render() patches this element in place and a
               closed-over input would be the detached one. */
            onclick: (e: Event) =>
              (e.currentTarget as HTMLElement).querySelector<HTMLInputElement>('input')?.click(),
            ondragover: (e: Event) => {
              e.preventDefault();
              (e.currentTarget as HTMLElement).classList.add('imp-drop--over');
            },
            ondragleave: (e: Event) =>
              (e.currentTarget as HTMLElement).classList.remove('imp-drop--over'),
            ondrop: (e: Event) => {
              e.preventDefault();
              (e.currentTarget as HTMLElement).classList.remove('imp-drop--over');
              accept((e as DragEvent).dataTransfer?.files?.[0]);
            },
          },
          h('span', { class: 'imp-drop__icon', 'aria-hidden': 'true', text: '↑' }),
          h(
            'span',
            { class: 'imp-drop__text' },
            h('span', { class: 'imp-drop__lead', text: 'Choose a CSV file, or drop one here' }),
            h('span', {
              class: 'imp-drop__sub',
              text: hasText
                ? `${s.importCsv.trim().split(/\n/).length - 1} row(s) of text loaded`
                : 'You can also paste the text below',
            }),
          ),
          h('input', {
            type: 'file',
            accept: '.csv,text/csv,text/plain',
            hidden: true,
            tabindex: -1,
            onchange: (e: Event) => accept((e.target as HTMLInputElement).files?.[0]),
          }),
        ),

        /* ---- the text ----
           Open while there is nothing to report, because pasting is the other way
           in. Collapsed once a plan exists: the report is the subject then, and
           the text is what it was computed from. */
        plan
          ? h(
              'details',
              { class: 'imp-src' },
              h('summary', { class: 'imp-src__sum', text: 'Show the file text' }),
              h('textarea', {
                class: 'input imp-text',
                rows: 6,
                'aria-label': 'CSV text',
                value: s.importCsv,
                oninput: (e: Event) =>
                  actions.setImportCsv((e.target as HTMLTextAreaElement).value),
              }),
            )
          : h('textarea', {
              class: 'input imp-text',
              rows: 8,
              placeholder:
                'id,name,route,roadName,km,district,province,river,type,spans,length,deckWidth,carriageway,year',
              'aria-label': 'CSV text',
              value: s.importCsv,
              oninput: (e: Event) => actions.setImportCsv((e.target as HTMLTextAreaElement).value),
            }),

        /* ---- the report ---- */
        plan
          ? h(
              'div',
              { class: 'imp-report' },
              tally(plan),
              /* A header nobody recognised is reported rather than guessed at.
                 A column silently going to the wrong field is the failure mode
                 that matters in a spreadsheet somebody exported. */
              plan.unknownColumns.length
                ? h('div', {
                    class: 'dlg__note dlg__note--error imp-unknown',
                    text: `Ignored columns this build does not know: ${plan.unknownColumns.join(', ')}.`,
                  })
                : null,
              h(
                'div',
                { class: 'imp-rows' },
                h(
                  'table',
                  { class: 'tbl imp-tbl' },
                  h(
                    'thead',
                    {},
                    h(
                      'tr',
                      {},
                      h('th', { text: 'Line' }),
                      h('th', { text: 'Asset id' }),
                      h('th', { text: 'Verdict' }),
                      h('th', { text: 'What would happen' }),
                    ),
                  ),
                  h('tbody', {}, ...plan.rows.map(verdictRow)),
                ),
              ),
            )
          : null,
      ),

      h(
        'div',
        { class: 'dlg__foot' },
        /* The refusal, said where the button is rather than only by disabling
           it. A greyed control with no sentence beside it is a dead end. */
        rejected > 0 && !applied
          ? h('span', {
              class: 'imp-block',
              text: `${rejected} row(s) would be rejected. Nothing is written until the file is clean.`,
            })
          : null,
        h('span', { class: 'spacer' }),
        button('Close', { onClick: () => actions.closeImport(), disabled: s.importBusy }),
        button(s.importBusy ? 'Reading…' : plan ? 'Check again' : 'Check the file', {
          primary: !plan && !s.importBusy,
          onClick: () => void actions.runImport(false),
          disabled: s.importBusy || !hasText,
        }),
        /* Only offered once a report is on screen, and never while anything
           would be rejected — a partial import leaves somebody reconciling a
           spreadsheet against a register by hand. */
        plan && !applied
          ? button(rejected ? 'Import' : `Import ${writes} row(s)`, {
              primary: rejected === 0,
              disabled: s.importBusy || rejected > 0 || writes === 0,
              onClick: () => void actions.runImport(true),
            })
          : null,
      ),
    ),
  );
}
